import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { formatCurrency } from 'utils/currency'
import { formatWeight } from 'utils/weight'
import { Button } from 'components/Button'
import { hasWeightLimit, ProductDetail } from 'models/Product'
import { ProductCounter } from '../ProductCounter'
import { ProductName } from './MainDetails.styles'
import { useAddToCart } from 'features/ECommerce/hooks/useAddToCart'
import { Note } from 'components/Note'
import { Icon } from 'components/Icon'
import { Proteins } from 'models/Pet'
import { Description } from '../Description'

const proteinToIcon: Record<Proteins, 'cow' | 'chicken' | 'deer' | 'lamb' | 'duck' | 'turkey'> = {
  [Proteins.BEEF]: 'cow',
  [Proteins.CHICKEN]: 'chicken',
  [Proteins.DUCK]: 'duck',
  [Proteins.TURKEY]: 'turkey',
  [Proteins.VENISON]: 'deer',
  [Proteins.LAMB]: 'lamb',
} as const

type MainDetailsProps = {
  productDetail: ProductDetail
  quantity: number
  onQuantityChange: (quantity: number) => void
}

export const MainDetails = ({ productDetail, quantity, onQuantityChange }: MainDetailsProps) => {
  const { t } = useTranslation()

  const addToCart = useAddToCart()
  const isOutOfStock = productDetail.isHidden

  return (
    <Stack gap="5" direction="column" alignItems="flex-start">
      <Stack direction="column" gap={{ '@initial': '1', '@bp2': '2' }}>
        <Stack direction="column" alignItems="flex-start" gap={{ '@initial': '0', '@bp2': '3' }}>
          <ProductName>
            <Text
              as="h1"
              size={{ '@initial': 'headline', '@bp2': 'h3' }}
              weight="bold"
              color="primary-800"
            >
              {t(`e-commerce:detail.product.${productDetail.slug}.data.name`)}
            </Text>
          </ProductName>
        </Stack>

        <Description
          productDescription={t(`e-commerce:detail.product.${productDetail.slug}.data.description`)}
        />
      </Stack>

      <Stack gap={{ '@initial': '2', '@bp2': '5' }} direction="column">
        {productDetail.proteins.length > 1 && (
          <Stack direction="column">
            {productDetail.proteins.map((protein) => {
              return (
                <Stack alignItems="center" key={protein}>
                  <Icon size="4" name={proteinToIcon[protein]} />
                  <Text size="headline" color="primary-800">
                    {t(`e-commerce:detail.proteins.${protein}`)}
                  </Text>
                </Stack>
              )
            })}
          </Stack>
        )}

        <ProductCounter
          onChange={(_, quantity) => onQuantityChange(quantity)}
          product={productDetail}
          quantity={quantity}
        />

        <Stack direction="column" gap="2">
          <Stack gap="3">
            <Text color="primary-500">
              <Stack gap="2">
                {productDetail.price.discount > 0 && (
                  <Text size="headline" weight="medium" lineThrough>
                    &nbsp;{formatCurrency(productDetail.price.subtotal)}&nbsp;
                  </Text>
                )}

                <span>
                  <Text size="headline" weight="medium">
                    {formatCurrency(productDetail.price.total)}
                  </Text>
                  {!productDetail.price.discount && (
                    <Text size="small">{t('common:per-unit')}</Text>
                  )}
                </span>
              </Stack>
            </Text>

            {hasWeightLimit(productDetail) && (
              <Text size="headline" color="primary-800">
                {`${formatWeight(productDetail.weight)}${t('e-commerce:per-unit')}`}
              </Text>
            )}
          </Stack>

          {productDetail.price.discount > 0 && (
            <Note>
              <Text color="tertiary-900">
                <Text size="headline" weight="medium">
                  Subscribe at checkout and save.{' '}
                </Text>
                <Text>Discount applies to first orders only.</Text>
              </Text>
            </Note>
          )}
        </Stack>
      </Stack>

      <Button
        size={{ '@initial': 'small', '@bp1': 'regular' }}
        css={{ width: '100%' }}
        onClick={() => addToCart(productDetail.productId, quantity, productDetail.name)}
        className="product-cta"
        disabled={isOutOfStock}
      >
        {isOutOfStock ? t('e-commerce:detail.out-of-stock') : t('e-commerce:detail.cta')}
      </Button>

      {hasWeightLimit(productDetail) && (
        <Note>
          <Text color="tertiary-900">{t('e-commerce:detail.reminder')}</Text>
        </Note>
      )}
    </Stack>
  )
}

MainDetails.displayName = 'MainDetails'
